///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Launch handling for share links (?s=<slug>). The slug is resolved
/// against the public API and translated into the existing self-contained
/// deep-link params (?lat/?lon/?alt, ?layers=, ?target=) via replaceState
/// BEFORE the app (and engine) mounts, so the tested launch paths do all
/// the camera/layer/focus work. The page render is gated on resolution —
/// the hook returns false while the fetch is in flight.
///
/// Placemark shares additionally stage a preview placemark in the dialog
/// slice (rendered by PlacemarksLayer without entering the user's data);
/// dataset shares download the file and run it through the normal import
/// pipeline. Every share sets dialog.activeShare, which drives the
/// branded recipient banner (SharedContentBanner).
///

import { CSVFileHandler, GeoJSONFileHandler } from "@/components/features/file-drop/handlers"
import { mountImageOverlay } from "@/utils/imageOverlayMount"
import { setActiveShare, setSharedPlacemarkPreview, setSharedStoryPreview, updateDatasetWindow } from "@/redux/dialogSlice"
import type { Placemark } from "@/redux/placemarksSlice"
import { resolvePublicShare } from "@/redux/sharesApi"
import { storyFromSharePayload } from "@/redux/storiesApi"
import { appStore, useAppDispatch } from "@/redux/store"
import { setCameraPoseParams } from "@/utils/cameraPoseUrl"
import { datasetUrl } from "@/utils/datasetUrl"
import type {
	ResolveShareResponse,
	ShareDatasetPayload,
	SharePlacemarkPayload,
	ShareStoryPayload,
	ShareViewPayload,
} from "@shared-types"
import { useEffect, useState } from "react"

function launchSlug(): string | null {
	return new URL(window.location.href).searchParams.get("s")
}

/**
 * Resolve a launch share link. Returns false while resolution is in
 * flight (callers should hold app render so the engine sees the
 * rewritten URL params); true when there is nothing to do or it's done.
 */
export function useShareLaunch(): boolean {
	const dispatch = useAppDispatch()
	const [ready, setReady] = useState(() => launchSlug() === null)

	useEffect(() => {
		if (ready) return
		const slug = launchSlug()
		if (!slug) {
			setReady(true)
			return
		}

		void (async () => {
			try {
				const share = await resolvePublicShare(slug)
				applyShare(share)
				dispatch(setActiveShare({ slug: share.slug, shareType: share.share_type, title: share.title }))
			} catch (error) {
				console.warn(`[shares] Could not resolve share link ${slug}:`, error)
				clearShareParam()
			}
			setReady(true)
		})()
	}, [ready, dispatch])

	return ready
}

/** Translate the share into the existing launch deep-link params */
function applyShare(share: ResolveShareResponse): void {
	const url = new URL(window.location.href)
	url.searchParams.delete("s")

	if (share.share_type === "view") {
		const payload = share.payload as ShareViewPayload
		setCameraPoseParams(url, payload.camera)
		if (payload.layers && payload.layers.length > 0) {
			url.searchParams.set("layers", payload.layers.join(","))
		}
		if (payload.focus_target_id) {
			url.searchParams.set("target", payload.focus_target_id)
		}
	} else if (share.share_type === "placemark") {
		const payload = share.payload as SharePlacemarkPayload
		appStore.dispatch(setSharedPlacemarkPreview(previewFromPayload(payload, share.created_at)))
		url.searchParams.set("layers", "placemark")
		url.searchParams.set("target", `pin-${payload.placemark.id}`)
	} else if (share.share_type === "story") {
		// Stage the snapshot as a playable preview (no import) and let the
		// story deep-link launch path auto-start playback
		const payload = share.payload as ShareStoryPayload
		const story = storyFromSharePayload(payload, share.created_at)
		if (story) {
			appStore.dispatch(setSharedStoryPreview(story))
			url.searchParams.set("target", `story-${story.id}`)
		}
	} else {
		// Dataset: download and import through the normal drop pipeline
		// (async — the dataset window appears when parsing completes)
		const payload = share.payload as ShareDatasetPayload
		void importSharedDataset(payload)
	}

	window.history.replaceState(null, "", url.toString())
}

function clearShareParam(): void {
	const url = new URL(window.location.href)
	url.searchParams.delete("s")
	window.history.replaceState(null, "", url.toString())
}

/** Share snapshot (wire shape) -> client placemark for the preview pin */
function previewFromPayload(payload: SharePlacemarkPayload, createdAt: string): Placemark {
	const p = payload.placemark
	return {
		id: p.id,
		name: p.name,
		color: p.color,
		note: p.note,
		anchorId: p.anchor_id,
		locations: p.locations.map(l => ({
			id: l.id,
			lat: l.lat,
			lon: l.lon,
			name: l.name,
			altitudeM: l.altitudeM,
			note: l.note,
		})),
		tags: [],
		createdAt,
		updatedAt: createdAt,
	}
}

async function importSharedDataset(payload: ShareDatasetPayload): Promise<void> {
	try {
		const response = await fetch(datasetUrl(payload.cdn_url))
		if (!response.ok) {
			throw new Error(`Download failed (${response.status})`)
		}
		const blob = await response.blob()
		const file = new File([blob], payload.filename, {
			type: payload.format === "geojson"
				? "application/geo+json"
				: payload.format === "image" ? (blob.type || "image/png") : "text/csv",
		})

		// Image ground overlays skip the tabular pipeline: drape at the
		// owner's stored georeference, cloud-linked for re-share/stories
		if (payload.format === "image") {
			await mountImageOverlay(appStore.dispatch, () => appStore.getState(), file, {
				bounds: payload.bounds ?? undefined,
				cloudDataset: {
					id: payload.dataset_id,
					name: payload.name,
					filename: payload.filename,
					format: payload.format,
					cdnUrl: payload.cdn_url,
				},
			})
			return
		}

		const context = { dispatch: appStore.dispatch, getState: () => appStore.getState() }
		const handler = payload.format === "geojson"
			? new GeoJSONFileHandler(context)
			: new CSVFileHandler(context)
		handler.initialize()
		try {
			const windowId = await handler.handleFile(file)
			// Link the window to its cloud identity so a recipient's story
			// capture can reference the dataset too
			if (windowId) {
				appStore.dispatch(updateDatasetWindow({
					id: windowId,
					updates: {
						cloudDataset: {
							id: payload.dataset_id,
							name: payload.name,
							filename: payload.filename,
							format: payload.format,
							cdnUrl: payload.cdn_url,
							featureCount: payload.feature_count ?? undefined,
						},
					},
				}))
			}
		} finally {
			handler.cleanup()
		}
	} catch (error) {
		console.warn(`[shares] Could not import shared dataset:`, error)
	}
}
